import { useState, useEffect, useCallback } from 'react';
import { vendorPaymentAPI } from '../../lib/api';
import { Button } from '../ui/button';
import { Input } from '../ui/input';
import { Label } from '../ui/label';
import { Badge } from '../ui/badge';
import { Switch } from '../ui/switch';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../ui/select';
import { toast } from 'sonner';
import { Loader2, Boxes, RefreshCw, Pencil, Check, X } from 'lucide-react';

const LOT_FILE_EXT_RE = /\.(xlsx|csv)$/i;

function LotLineRow({ line, canCorrect, onCorrect }) {
  const [editing, setEditing] = useState(false);
  const [sku, setSku] = useState('');
  const [saving, setSaving] = useState(false);

  const save = async () => {
    if (!sku.trim()) return;
    setSaving(true);
    try {
      await onCorrect(line.id, sku.trim());
      setEditing(false);
    } catch (err) {
      // Keep the editor open so the typed SKU isn't lost on a failed correction.
    } finally {
      setSaving(false);
    }
  };

  return (
    <tr data-testid={`vp-lot-line-${line.id}`} className={line.match_status === 'NO_MATCH' ? 'bg-amber-50' : ''}>
      <td className="p-2 text-sm max-w-[260px] truncate" title={line.uploaded_text}>{line.uploaded_text}</td>
      <td className="p-2 text-sm text-right">{line.qty ?? '—'}</td>
      <td className="p-2 text-sm text-right">{line.excel_price != null ? line.excel_price : '—'}</td>
      <td className="p-2 text-sm">
        {editing ? (
          <div className="flex items-center gap-1">
            <Input value={sku} onChange={(e) => setSku(e.target.value)} placeholder="SKU" className="h-7 text-xs w-28" data-testid={`vp-lot-line-sku-input-${line.id}`} />
            <Button size="icon" className="h-7 w-7 rounded-sm shrink-0" disabled={saving} onClick={save} data-testid={`vp-lot-line-save-${line.id}`}><Check className="h-3.5 w-3.5" /></Button>
            <Button size="icon" variant="outline" className="h-7 w-7 rounded-sm shrink-0" onClick={() => setEditing(false)} data-testid={`vp-lot-line-cancel-${line.id}`}><X className="h-3.5 w-3.5" /></Button>
          </div>
        ) : (
          <span className="font-mono text-xs">{line.matched_sku || <span className="text-muted-foreground italic">unmatched</span>}</span>
        )}
      </td>
      <td className="p-2 text-xs">
        <Badge className={`rounded-sm text-[10px] ${line.match_status === 'MATCHED' ? 'bg-emerald-100 text-emerald-800' : line.match_status === 'CORRECTED' ? 'bg-sky-100 text-sky-800' : 'bg-amber-100 text-amber-800'}`}>
          {line.match_status} {line.match_status !== 'NO_MATCH' && line.match_confidence != null ? `(${line.match_confidence}%)` : ''}
        </Badge>
      </td>
      <td className="p-2">
        {canCorrect && !editing && (
          <button onClick={() => { setSku(line.matched_sku || ''); setEditing(true); }} data-testid={`vp-lot-line-edit-btn-${line.id}`} title="Change item">
            <Pencil className="h-3.5 w-3.5 text-muted-foreground" />
          </button>
        )}
      </td>
    </tr>
  );
}

export function LotItemsCard({ requestId, req, readOnly, canManage, onChanged }) {
  const [lotLines, setLotLines] = useState([]);
  const [loadingLines, setLoadingLines] = useState(false);
  const [unmatchedOnly, setUnmatchedOnly] = useState(false);
  const [rereading, setRereading] = useState(false);

  const files = (req.supporting_files || []).filter((f) => LOT_FILE_EXT_RE.test(f.name || ''));

  const loadLines = useCallback(async () => {
    if (!req.is_lot_pi || req.lot_status !== 'DONE') { setLotLines([]); return; }
    setLoadingLines(true);
    try {
      const res = await vendorPaymentAPI.getLotLines(requestId);
      setLotLines(res.data.lot_lines || []);
    } catch (err) {
      toast.error('Could not load lot items');
    } finally {
      setLoadingLines(false);
    }
  }, [requestId, req.is_lot_pi, req.lot_status]);

  useEffect(() => { loadLines(); }, [loadLines]);

  const toggleLot = async (checked) => {
    try {
      await vendorPaymentAPI.updateHeader(requestId, { is_lot_pi: checked, ...(checked ? {} : { lot_file_id: null }) });
      onChanged();
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Could not change lot mode');
    }
  };

  const pickFile = async (fileId) => {
    try {
      await vendorPaymentAPI.updateHeader(requestId, { lot_file_id: fileId });
      toast.success('Lot file chosen');
      onChanged();
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Could not choose this lot file');
    }
  };

  const reread = async () => {
    if (lotLines.some((l) => l.match_status === 'CORRECTED') && !window.confirm('Re-reading the lot file will discard any manual corrections made so far. Continue?')) {
      return;
    }
    setRereading(true);
    try {
      await vendorPaymentAPI.rereadLotFile(requestId);
      toast.success('Lot file queued for re-reading');
      onChanged();
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Could not re-read the lot file');
    } finally {
      setRereading(false);
    }
  };

  const correctLine = async (lineId, sku) => {
    try {
      await vendorPaymentAPI.correctLotLine(requestId, lineId, sku);
      toast.success('Lot line corrected');
      await loadLines();
      onChanged();
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Could not correct this line');
      throw err;
    }
  };

  if (!canManage && !req.is_lot_pi) return null;

  const summary = req.lot_summary;
  const shownLines = unmatchedOnly ? lotLines.filter((l) => l.match_status === 'NO_MATCH') : lotLines;

  return (
    <div className="rounded-sm border border-violet-200 bg-violet-50/40 p-3" data-testid="vp-lot-card">
      <div className="flex items-center justify-between mb-2">
        <p className="text-xs font-semibold uppercase text-violet-900 flex items-center gap-1.5"><Boxes className="h-3.5 w-3.5" />Lot PI (priced from attached Excel)</p>
        {canManage && (
          <div className="flex items-center gap-2">
            <Switch checked={!!req.is_lot_pi} onCheckedChange={toggleLot} data-testid="vp-lot-toggle" />
            <span className="text-xs text-muted-foreground">Lot mode</span>
          </div>
        )}
      </div>

      {req.is_lot_pi && canManage && (
        <div className="mb-2">
          <Label className="text-[11px] text-muted-foreground uppercase">Lot file (.xlsx or .csv supporting file)</Label>
          <Select value={req.lot_file_id || ''} onValueChange={pickFile}>
            <SelectTrigger className="h-8 text-sm" data-testid="vp-lot-file-select"><SelectValue placeholder="Choose a supporting file" /></SelectTrigger>
            <SelectContent>
              {files.length === 0 && <div className="px-2 py-1.5 text-xs text-muted-foreground">Attach an .xlsx or .csv supporting file first</div>}
              {files.map((f) => <SelectItem key={f.file_id} value={f.file_id}>{f.name}</SelectItem>)}
            </SelectContent>
          </Select>
        </div>
      )}

      {req.is_lot_pi && !req.lot_file_id && (
        <p className="text-sm text-red-600" data-testid="vp-lot-file-missing-note">No lot file chosen yet — choose one above, then run Check.</p>
      )}
      {req.is_lot_pi && req.lot_status === 'PENDING' && (
        <p className="text-sm text-amber-700" data-testid="vp-lot-pending-note">Lot file chosen — reading and matching shortly.</p>
      )}
      {req.is_lot_pi && req.lot_status === 'MATCHING' && (
        <p className="text-sm text-amber-700 flex items-center gap-1.5" data-testid="vp-lot-matching-note"><Loader2 className="h-3.5 w-3.5 animate-spin" />Matching lot items in the background — refresh shortly.</p>
      )}
      {req.is_lot_pi && req.lot_status === 'FAILED' && (
        <div className="text-sm text-red-600 flex items-center gap-2" data-testid="vp-lot-failed-note">
          Could not read the lot file.
          {canManage && <Button size="sm" variant="outline" className="rounded-sm h-7" onClick={reread} disabled={rereading} data-testid="vp-lot-reread-btn">{rereading ? <Loader2 className="h-3.5 w-3.5 animate-spin mr-1" /> : <RefreshCw className="h-3.5 w-3.5 mr-1" />}Re-read file</Button>}
        </div>
      )}

      {req.is_lot_pi && req.lot_status === 'DONE' && summary && (
        <div className="space-y-2">
          <div className="flex items-center justify-between text-xs text-violet-900">
            <span data-testid="vp-lot-summary">{summary.rows_matched}/{summary.rows_read} rows matched · qty {summary.qty_matched}/{summary.qty_total}{summary.excel_value_total != null ? ` · Excel value ${summary.excel_value_total}` : ''}</span>
            <div className="flex items-center gap-2">
              <label className="flex items-center gap-1 cursor-pointer">
                <input type="checkbox" checked={unmatchedOnly} onChange={(e) => setUnmatchedOnly(e.target.checked)} data-testid="vp-lot-unmatched-filter" />
                Unmatched only
              </label>
              {canManage && <Button size="sm" variant="outline" className="rounded-sm h-7" onClick={reread} disabled={rereading} data-testid="vp-lot-reread-btn">{rereading ? <Loader2 className="h-3.5 w-3.5 animate-spin mr-1" /> : <RefreshCw className="h-3.5 w-3.5 mr-1" />}Re-read file</Button>}
            </div>
          </div>
          {loadingLines ? (
            <div className="flex justify-center py-4"><Loader2 className="h-4 w-4 animate-spin text-muted-foreground" /></div>
          ) : (
            <div className="rounded-sm border border-border overflow-x-auto max-h-80 overflow-y-auto">
              <table className="w-full text-sm">
                <thead className="bg-muted/50 sticky top-0">
                  <tr className="text-left text-[11px] uppercase text-muted-foreground">
                    <th className="p-2">Uploaded Text</th><th className="p-2 text-right">Qty</th><th className="p-2 text-right">Excel Price</th>
                    <th className="p-2">Matched SKU</th><th className="p-2">Status</th><th className="p-2"></th>
                  </tr>
                </thead>
                <tbody>
                  {shownLines.map((ln) => <LotLineRow key={ln.id} line={ln} canCorrect={canManage && !readOnly} onCorrect={correctLine} />)}
                </tbody>
              </table>
            </div>
          )}
        </div>
      )}
    </div>
  );
}
